import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Plus, RotateCcw, ShieldOff, Copy, KeyRound } from "lucide-react";

const AVAILABLE_SCOPES = [
  "openid", "profile", "email",
  "read:time_entries", "write:time_entries",
  "read:timesheets", "read:worker_profile",
];

export default function OAuthClientsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState(null); // { id, name, client_secret }
  const [showCreate, setShowCreate] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenant/oauth-clients");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const rotate = async (c) => {
    try {
      const { data } = await api.post(`/tenant/oauth-clients/${c.id}/rotate-secret`);
      setReveal({ id: c.id, name: c.name, client_secret: data.client_secret });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revoke = async (c) => {
    try {
      await api.delete(`/tenant/oauth-clients/${c.id}`);
      toast.success(`Revoked ${c.name}`);
      setConfirmRevoke(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="oauth-clients-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
            Integrations
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">
            OAuth 2.0 clients
          </h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Register partner apps that can &ldquo;Sign in with Quanta.&rdquo; Each client gets its own
            <code className="mx-1">client_id</code> and (for confidential apps) a secret shown once.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="btn-new-oauth-client">
          <Plus className="mr-2 h-4 w-4" />
          New client
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            Registered clients
            <Badge variant="outline" data-testid="oauth-clients-count">{items.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading&hellip;
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="oauth-clients-empty">
              No OAuth clients yet. Create one to let partner apps sign in with Quanta.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Client ID</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Scopes</TableHead>
                  <TableHead>Redirect URIs</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} data-testid={`oauth-client-row-${c.id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="font-mono text-xs">{c.id}</TableCell>
                    <TableCell>
                      <Badge variant={c.client_type === "confidential" ? "default" : "outline"}>
                        {c.client_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {(c.allowed_scopes || []).join(" ")}
                    </TableCell>
                    <TableCell className="max-w-[220px] truncate text-xs">
                      {(c.redirect_uris || []).join(", ")}
                    </TableCell>
                    <TableCell>
                      {c.revoked_at ? (
                        <Badge variant="destructive">Revoked</Badge>
                      ) : (
                        <Badge variant="secondary">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      {!c.revoked_at && c.client_type === "confidential" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => rotate(c)}
                          data-testid={`btn-rotate-${c.id}`}
                        >
                          <RotateCcw className="mr-1 h-3 w-3" /> Rotate
                        </Button>
                      )}
                      {!c.revoked_at && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setConfirmRevoke(c)}
                          data-testid={`btn-revoke-${c.id}`}
                        >
                          <ShieldOff className="mr-1 h-3 w-3" /> Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateClientDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(payload) => {
          setShowCreate(false);
          if (payload?.client_secret) {
            setReveal({
              id: payload.id,
              name: payload.name,
              client_secret: payload.client_secret,
            });
          }
          load();
        }}
      />

      <RevealDialog reveal={reveal} onClose={() => setReveal(null)} />

      <Dialog open={!!confirmRevoke} onOpenChange={(o) => !o && setConfirmRevoke(null)}>
        <DialogContent data-testid="dialog-confirm-revoke">
          <DialogHeader>
            <DialogTitle>Revoke {confirmRevoke?.name}?</DialogTitle>
            <DialogDescription>
              This immediately invalidates every access + refresh token issued for this client.
              Users of the partner app will be signed out.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRevoke(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => revoke(confirmRevoke)}
              data-testid="btn-confirm-revoke"
            >
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateClientDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [clientType, setClientType] = useState("public");
  const [redirectUris, setRedirectUris] = useState("");
  const [scopes, setScopes] = useState(["openid", "profile", "email"]);
  const [saving, setSaving] = useState(false);

  const toggleScope = (s) => {
    setScopes((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  };

  const submit = async () => {
    const uris = redirectUris
      .split(/[\s,]+/)
      .map((u) => u.trim())
      .filter(Boolean);
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (uris.length === 0) { toast.error("At least one redirect URI"); return; }
    if (scopes.length === 0) { toast.error("Pick at least one scope"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/tenant/oauth-clients", {
        name: name.trim(),
        client_type: clientType,
        redirect_uris: uris,
        allowed_scopes: scopes,
      });
      toast.success(`Created ${name}`);
      onCreated(data);
      setName(""); setRedirectUris(""); setScopes(["openid", "profile", "email"]);
      setClientType("public");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="dialog-new-oauth-client">
        <DialogHeader>
          <DialogTitle>New OAuth 2.0 client</DialogTitle>
          <DialogDescription>
            Public = SPAs / mobile (PKCE required). Confidential = server-side apps that can safely hold a secret.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="oauth-name">App name</Label>
            <Input
              id="oauth-name"
              placeholder="Acme Payroll"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="input-oauth-client-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="oauth-type">Client type</Label>
            <Select value={clientType} onValueChange={setClientType}>
              <SelectTrigger id="oauth-type" data-testid="select-oauth-client-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public (SPA / mobile — PKCE)</SelectItem>
                <SelectItem value="confidential">Confidential (server-side, uses secret)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="oauth-redirects">Redirect URIs</Label>
            <Textarea
              id="oauth-redirects"
              placeholder="https://app.acme.com/callback&#10;https://staging.acme.com/callback"
              rows={3}
              value={redirectUris}
              onChange={(e) => setRedirectUris(e.target.value)}
              data-testid="input-oauth-redirect-uris"
            />
            <p className="text-xs text-muted-foreground">
              One per line. Exact match — no wildcards, case-sensitive.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Allowed scopes</Label>
            <div className="grid grid-cols-2 gap-2">
              {AVAILABLE_SCOPES.map((s) => (
                <label
                  key={s}
                  className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs"
                  data-testid={`scope-${s}`}
                >
                  <Checkbox
                    checked={scopes.includes(s)}
                    onCheckedChange={() => toggleScope(s)}
                  />
                  <code>{s}</code>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="btn-create-oauth-client">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create client
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevealDialog({ reveal, onClose }) {
  const copy = () => {
    if (!reveal?.client_secret) return;
    navigator.clipboard.writeText(reveal.client_secret);
    toast.success("Secret copied — store it now");
  };
  return (
    <Dialog open={!!reveal} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialog-oauth-secret-reveal">
        <DialogHeader>
          <DialogTitle>Client secret for {reveal?.name}</DialogTitle>
          <DialogDescription>
            Copy this now — Quanta will never show it again. Store it in your app&rsquo;s secret manager.
          </DialogDescription>
        </DialogHeader>
        <Alert>
          <AlertTitle className="font-mono text-xs">client_id</AlertTitle>
          <AlertDescription className="font-mono text-xs" data-testid="reveal-client-id">
            {reveal?.id}
          </AlertDescription>
        </Alert>
        <Alert>
          <AlertTitle className="font-mono text-xs">client_secret</AlertTitle>
          <AlertDescription className="break-all font-mono text-xs" data-testid="reveal-client-secret">
            {reveal?.client_secret}
          </AlertDescription>
        </Alert>
        <DialogFooter>
          <Button variant="outline" onClick={copy} data-testid="btn-copy-oauth-secret">
            <Copy className="mr-2 h-4 w-4" /> Copy secret
          </Button>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
